import { AppleResponseUser } from "@faire/deprecated-retailer-visitor-shared/components/SocialSignOnButtons/AppleLoginButton";
import { useFaireDirectBrand } from "@faire/deprecated-retailer-visitor-shared/hooks/FaireDirect/useFaireDirectBrand/useFaireDirectBrand";
import { SignUpType } from "@faire/deprecated-retailer-visitor-shared/lib/popSignUp";
import { useSettingSignUpSplit } from "@faire/deprecated-retailer-visitor-shared/settings/useSettingSignUpSplit";
import { useAssignSettingSignUpSplit } from "@faire/deprecated-retailer-visitor-shared/settings/useSettingSignUpSplit";
import { BLANK_SIGN_UP_SUGGESTIONS } from "@faire/deprecated-retailer-visitor-shared/stores/domain/SSO/SSOContextStore";
import { useSSOErrorStore } from "@faire/deprecated-retailer-visitor-shared/stores/domain/SSO/useSSOErrorStore";
import { useSSOStore } from "@faire/deprecated-retailer-visitor-shared/stores/domain/SSO/useSSOStore";
import { useRouter } from "@faire/retailer-visitor-shared/app/_lib/routing/useRouter";
import { AppModalEnv } from "@faire/retailer-visitor-shared/components/App/ModalTypes";
import login from "@faire/web-api--source/endpoints/www/api/v2/users/login/post";
import { trackSigninUsePasswordToLinkSso } from "@faire/web-api--source/events/signin/actionCategoryOther/usePasswordToLinkSso";
import { trackSigninSsoErrorError } from "@faire/web-api--source/events/signin/error/ssoError";
import { trackSigninTriggersMfaSubmit } from "@faire/web-api--source/events/signin/submit/triggersMfa";
import { trackSigninSsoSuccessSuccess } from "@faire/web-api--source/events/signin/success/ssoSuccess";
import { ILoginRequest } from "@faire/web-api--source/indigofair/data/ILoginRequest";
import { ILoginResponse } from "@faire/web-api--source/indigofair/data/ILoginResponse";
import { ISignUpSuggestions } from "@faire/web-api--source/indigofair/data/ISignUpSuggestions";
import { IUser } from "@faire/web-api--source/indigofair/data/IUser";
import { LoginState } from "@faire/web-api--source/indigofair/data/LoginState";
import { SocialSignOnProvider } from "@faire/web-api--source/indigofair/data/SocialSignOnProvider";
import signUpPersonal, {
  QueryParameters as SignUpQueryParameters,
} from "@faire/web-api--source/routes/www/welcome/r/personal";
import { getWebApiErrorMessage } from "@faire/web/common/getErrorMessage";
import { getLocationOrThrow } from "@faire/web/common/globals/getLocation";
import { useStrictLocalization } from "@faire/web/common/localization";
import { FAIRE_DIRECT_BRAND_QUERY_PARAM } from "@faire/web/common/routes/faire-direct/FAIRE_DIRECT_BRAND_QUERY_PARAM";
import { useSearchParams } from "next/navigation";

export interface ISocialSignOnButtonProps {
  onSignIn: (user: IUser) => void;
  onMFA: (mfaDescription: string, mfaTitle: string) => void;
  onSelectMFA: (mfaOptions: ILoginResponse.IMfaOption[]) => void;
  onConfirmPassword: (signUpSuggestionss?: ISignUpSuggestions) => void;
  sourceId: string;
  disabled?: boolean;
  isOnSignIn?: boolean;
  appModalEnv: AppModalEnv;
}

export const useHandleSSOSuccess = ({
  onSignIn,
  onSelectMFA,
  onMFA,
  onConfirmPassword,
  provider,
  sourceId,
}: Omit<ISocialSignOnButtonProps, "disabled"> & {
  provider: SocialSignOnProvider;
}) => {
  const { strictLocalize } = useStrictLocalization();
  const { setErrorMessage } = useSSOErrorStore();
  const { reset, set } = useSSOStore();
  const searchParams = useSearchParams();
  const router = useRouter();

  const queryParams = SignUpQueryParameters.parse(searchParams.toString());

  const {
    isFaireDirectActiveInLoggedOut,
    faireDirectBrand,
    faireDirectBrandToken,
    faireDirectBrandAlias,
  } = useFaireDirectBrand();

  const assignSettingSignUpSplit = useAssignSettingSignUpSplit();
  const isSignUpSplitEnabled = useSettingSignUpSplit();

  return async (id_token: string, appleResponseUser?: AppleResponseUser) => {
    const sso_token = {
      id_token,
      provider,
    };

    try {
      // Send token to login end point
      const {
        user,
        mfa_description,
        mfa_title,
        mfa_options,
        login_state,
        sign_up_suggestions,
      } = await login(
        ILoginRequest.build({
          sso_token,
          enable_mfa_method_selection: true,
        })
      );

      reset();
      const signUpSuggestions = generateSignUpSuggestions(
        provider,
        BLANK_SIGN_UP_SUGGESTIONS,
        sign_up_suggestions,
        appleResponseUser
      );
      set(signUpSuggestions, id_token, provider);

      const faireDirectParams = isFaireDirectActiveInLoggedOut
        ? {
            brand: faireDirectBrandToken,
            brandName: faireDirectBrand?.name,
            [FAIRE_DIRECT_BRAND_QUERY_PARAM]: faireDirectBrandAlias,
          }
        : {};

      const onSignUp = () => {
        assignSettingSignUpSplit();
        const redirectLocation = signUpPersonal({
          ...queryParams,
          ...faireDirectParams,
          signUp: isFaireDirectActiveInLoggedOut
            ? SignUpType.FAIRE_DIRECT
            : (queryParams.signUp ?? SignUpType.GENERIC),
          email: signUpSuggestions?.email_address,
        });

        if (isSignUpSplitEnabled) {
          getLocationOrThrow().href = redirectLocation;
        } else {
          router.replace(redirectLocation);
        }
        return;
      };

      trackSigninSsoSuccessSuccess(provider, sourceId, login_state);
      switch (login_state) {
        case LoginState.LOGIN_OK:
          if (user) {
            onSignIn(user);
          }
          break;
        case LoginState.SIGN_UP_REQUIRED:
          onSignUp();
          break;
        case LoginState.MFA_METHOD_SELECTION_REQUIRED:
          trackSigninTriggersMfaSubmit("SSO");
          onSelectMFA(mfa_options);
          break;
        case LoginState.MFA_REQUIRED:
          trackSigninTriggersMfaSubmit("SSO");
          onMFA(
            mfa_description ??
              strictLocalize({
                id: "H2iudf",
                defaultMessage:
                  "Almost there! Please enter the code sent to your email",
                description: {
                  text: "Message to inform user that they should check their email for a code to verify them",
                },
              }),
            mfa_title ??
              strictLocalize({
                id: "0dap3n",
                defaultMessage: "Enter verification code",
                description: {
                  text: "User is almost at the end of the log in process",
                },
              })
          );
          break;
        case LoginState.SOCIAL_LOGIN_PASSWORD_REQUIRED:
          trackSigninUsePasswordToLinkSso();
          onConfirmPassword(signUpSuggestions);
          break;
        default:
          // login_state is ISSUED_LOGIN_CODE or LOGIN_STATE_UNKNOWN
          setErrorMessage(
            strictLocalize({
              id: "EQShW8",
              defaultMessage: "Sorry, server was unable to process request.",
              description: {
                text: "Error message if server was unable to process request",
              },
            })
          );
          break;
      }
    } catch (error) {
      const defaultErrorMessage = strictLocalize({
        id: "67+pg9",
        defaultMessage: "Sorry, was unable to reach server",
        description: {
          text: "Error message if unable to connect to server",
        },
      });
      setErrorMessage(
        getWebApiErrorMessage(error, strictLocalize, defaultErrorMessage)
      );
      trackSigninSsoErrorError(provider, sourceId);
    }
  };
};

const generateSignUpSuggestions = (
  provider: SocialSignOnProvider,
  BLANK_SIGN_UP_SUGGESTIONS: ISignUpSuggestions,
  sign_up_suggestions?: ISignUpSuggestions,
  appleResponseUser?: AppleResponseUser
): ISignUpSuggestions | undefined => {
  if (provider !== SocialSignOnProvider.APPLE) {
    return sign_up_suggestions;
  }
  // For Apple
  let ssoStorePayload = BLANK_SIGN_UP_SUGGESTIONS;
  let email_address: string;
  if (appleResponseUser) {
    const {
      email,
      name: { firstName, lastName },
    } = appleResponseUser;
    email_address = sign_up_suggestions?.email_address ?? email;

    ssoStorePayload = {
      email_address,
      first_name: firstName,
      last_name: lastName,
      profile_image_url: "",
      provider,
    };
  } else {
    email_address = sign_up_suggestions?.email_address ?? "";

    ssoStorePayload = {
      ...ssoStorePayload,
      email_address,
      provider,
    };
  }
  return ssoStorePayload;
};
